import {
   RcsbFv,
   RcsbFvBoardConfigInterface,
   RcsbFvDisplayTypes,
   RcsbFvRowExtendedConfigInterface,
} from '@rcsb/rcsb-saguaro';
import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../api/client';
import { useAhojData } from '../contexts/AhojDataContext';
import { useStructureEventBus } from '../contexts/StructureEventBusContext';
import { useStructureState } from '../contexts/StructureStateContext';
import { ApoHoloSiteDto } from '../models/ApoHoloSiteDto';
import { StructureSequenceDto } from '../models/StructureSequenceDto';

interface PdbSequenceMappingApiResponse {
   sequence: string;
   uniprot_start?: number | null;
   uniprot_end?: number | null;
}

/**
 * Embeds the RCSB Saguaro sequence viewer (`RcsbFv`) into the control panel.
 *
 * Architecture overview:
 * - One **UniProt reference track** is shown at the top. PDB-chain tracks are
 *   positioned below it, aligned to the UniProt coordinates using
 *   `uniprotStart`/`uniprotEnd` offsets from `StructureSequenceDto`.
 * - **Pocket residue blocks** are overlaid on each PDB track as a BLOCK annotation
 *   row derived from `PocketResiduesResult`.
 * - **Click on a residue** in the viewer fires `RESIDUE_FOCUS` on the event bus;
 *   the corresponding residue in the Molstar viewport is highlighted.
 * - **Hover on a residue** fires `UNIPROT_HOVER` to highlight the position across
 *   all visible structures in Molstar.
 * - **Selection of a UniProt position** fires `SELECT_UNIPROT_POSITION`.
 *
 * Sync-prevention refs:
 * - `skipNextFocusSyncRef` — incremented before dispatching a Molstar focus action
 *   that would otherwise bounce back via the `structureClick` event and
 *   re-trigger the same focus.
 * - `suppressStructureClickRef` — prevents the click event from Molstar viewport
 *   from re-initialising the RCSB viewer when we already know the source.
 *
 * The RCSB viewer re-renders asynchronously after track data changes, so
 * `injectRemoveButtons` uses a `MutationObserver` to re-inject custom buttons
 * after each RCSB DOM rebuild.
 */
const SequenceViewerComponent = (): React.JSX.Element => {
   const { residueMapping, uniprotSequenceMap, selectedPdb, setSelectedPdb, selectedUniprot, apoHoloSites } =
      useAhojData();
   const { dispatch, dispatchStructureClick, subscribeStructureClick, subscribe, subscribeMolstarHover } =
      useStructureEventBus();
   const { controlsMap } = useStructureState();

   const [activePdbSequences, setActivePdbSequences] = useState<StructureSequenceDto[]>([]);
   const [activeBaseSequence, setActiveBaseSequence] = useState<StructureSequenceDto | null>(null);
   const [focusedStructureRowIds, setFocusedStructureRowIds] = useState<Set<string>>(new Set());
   const [focusedPocketRowId, setFocusedPocketRowId] = useState<string | null>(null);

   const focusedStructureRowIdsRef = useRef<Set<string>>(new Set());
   const focusedPocketRowIdRef = useRef<string | null>(null);
   const suppressStructureClickRef = useRef(0);

   /**
    * Toggles the focused structure. If `rowId` is already the focused structure
    * the focus is cleared (returns `false`). Otherwise the new `rowId` is set as
    * the focused structure (returns `true`).
    *
    * Used to determine whether the RCSB viewer should be re-initialised with the
    * new structure's sequence tracks or whether the current view should be cleared.
    */
   const toggleFocusedStructure = (rowId: string): boolean => {
      const prev = focusedStructureRowIdsRef.current;
      const nowFocused = !prev.has(rowId);
      const next = new Set(prev);
      if (nowFocused) next.add(rowId);
      else next.delete(rowId);
      focusedStructureRowIdsRef.current = next;
      setFocusedStructureRowIds(next);
      return nowFocused;
   };
   /**
    * Updates the focused pocket row ID state. Passing `null` clears the focused
    * pocket so no residue blocks are highlighted.
    */
   const setFocusedPocket = (rowId: string | null): void => {
      focusedPocketRowIdRef.current = rowId;
      setFocusedPocketRowId(rowId);
   };

   const sequenceViewerId = 'sequence-viewer';
   const rcsbFvRef = useRef<RcsbFv | null>(null);
   const wrapperRef = useRef<HTMLDivElement>(null);
   const savedScrollRef = useRef<number>(0);
   const observerRef = useRef<MutationObserver | null>(null);
   const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const selectedUniprotPosRef = useRef<number | null>(null);
   const skipNextFocusSyncRef = useRef(false);

   const findSiteByRowId = (rowId: string): ApoHoloSiteDto | undefined => {
      return [
         ...apoHoloSites.apo_sites,
         ...apoHoloSites.holo_sites,
         ...apoHoloSites.unobserved_sites,
         ...apoHoloSites.query_pocket_sites,
         ...apoHoloSites.alphafold_sites,
      ].find((s) => s.id === rowId);
   };

   const clearContainer = (): void => {
      if (wrapperRef.current) {
         wrapperRef.current.innerHTML = '';
      }
   };

   const getOrCreateInner = (): HTMLDivElement | null => {
      if (!wrapperRef.current) return null;
      wrapperRef.current.innerHTML = '';
      const inner = document.createElement('div');
      inner.id = sequenceViewerId;
      wrapperRef.current.appendChild(inner);
      return inner;
   };

   useEffect(() => {
      if (selectedPdb.length === 0) {
         setActivePdbSequences([]);
         return;
      }

      let cancelled = false;

      const fetchAll = async (): Promise<void> => {
         const results: StructureSequenceDto[] = [];
         for (const pdb of selectedPdb as Array<{ pdbOrUniprotId: string; rowId: string; chainId?: string }>) {
            try {
               const chainId = typeof pdb.chainId === 'string' ? pdb.chainId : '';
               const basePath = `${API_URL}/api/proxy/rcsb-fasta/${pdb.pdbOrUniprotId.toUpperCase()}`;
               const urlStr = chainId.length > 0 ? `${basePath}?chain_id=${encodeURIComponent(chainId)}` : basePath;

               const res = await fetch(urlStr);
               if (!res.ok) continue;

               const data = (await res.json()) as PdbSequenceMappingApiResponse;
               if (!data.sequence) continue;

               const chainLabel = chainId.length > 0 ? chainId : null;
               results.push({
                  id: pdb.pdbOrUniprotId,
                  rowId: pdb.rowId,
                  label: chainLabel ? `${pdb.pdbOrUniprotId} (${chainLabel})` : pdb.pdbOrUniprotId,
                  sequence: data.sequence,
                  source: 'pdb',
                  uniprotStart: data.uniprot_start ?? undefined,
                  uniprotEnd: data.uniprot_end ?? undefined,
               });
            } catch {
               // skip failed fetches
            }
         }
         if (!cancelled) setActivePdbSequences(results);
      };

      void fetchAll();

      return () => {
         cancelled = true;
      };
   }, [selectedPdb]);

   useEffect(() => {
      if (!selectedUniprot || !selectedUniprot.pdbOrUniprotId) {
         setActiveBaseSequence(null);
         return;
      }

      const cached = uniprotSequenceMap.get(selectedUniprot.pdbOrUniprotId);
      if (cached) {
         setActiveBaseSequence(cached);
         return;
      }

      let cancelled = false;
      fetch(`${API_URL}/api/proxy/uniprot-fasta/${selectedUniprot.pdbOrUniprotId}`)
         .then((res) => res.text())
         .then((text) => {
            if (cancelled) return;
            const dto = parseFastaFirst(text, selectedUniprot.pdbOrUniprotId, selectedUniprot.rowId, 'uniprot');
            if (dto) {
               setActiveBaseSequence(dto);
            }
         })
         .catch(() => {
            if (cancelled) return;
            setActiveBaseSequence(null);
         });

      return () => {
         cancelled = true;
      };
   }, [selectedUniprot, uniprotSequenceMap]);

   // Sync focusedStructureRowIds with FOCUS actions dispatched from table/hide operations.
   // pdbBlockTrack sets skipNextFocusSyncRef before its own dispatch to avoid conflict with
   // its direct toggleFocusedStructure call.
   useEffect(() => {
      const unsub = subscribe((action) => {
         if (action.type !== 'FOCUS') return;
         if (skipNextFocusSyncRef.current) {
            skipNextFocusSyncRef.current = false;
            return;
         }
         const payload = action.payload as { rowId: string; value: boolean } | null;
         if (!payload) return;
         const { rowId, value } = payload;
         const isFocused = focusedStructureRowIdsRef.current.has(rowId);
         if (value === isFocused) return;
         const next = new Set(focusedStructureRowIdsRef.current);
         if (value) next.add(rowId);
         else next.delete(rowId);
         focusedStructureRowIdsRef.current = next;
         setFocusedStructureRowIds(next);
      });
      return unsub;
   }, [subscribe]);

   // When a structure is added to RCSB while already selected in the table, mark it focused.
   useEffect(() => {
      let changed = false;
      const next = new Set(focusedStructureRowIdsRef.current);
      for (const seq of activePdbSequences) {
         if (controlsMap.get(seq.rowId)?.isSelected && !next.has(seq.rowId)) {
            next.add(seq.rowId);
            changed = true;
         }
      }
      if (!changed) return;
      focusedStructureRowIdsRef.current = next;
      setFocusedStructureRowIds(next);
   }, [activePdbSequences, controlsMap]);

   useEffect(() => {
      const unsub = subscribeStructureClick((site) => {
         if (suppressStructureClickRef.current > 0) {
            suppressStructureClickRef.current--;
            return;
         }
         const match = activePdbSequences.find((seq) => seq.rowId === site.id);
         if (!match) return;
         // focusedStructureRowIdsRef is already updated by FOCUS subscribe before this fires
         const isNowFocused = focusedStructureRowIdsRef.current.has(site.id);
         if (!isNowFocused && focusedPocketRowIdRef.current === site.id) {
            dispatch({ type: 'POCKET_FOCUS', payload: { rowId: site.id, url: site.url, value: false } });
            setFocusedPocket(null);
         }
      });
      return unsub;
   }, [activePdbSequences, subscribeStructureClick, dispatch]);

   // Mirror Molstar residue hover into the RCSB sequence viewer.
   useEffect(() => {
      const unsub = subscribeMolstarHover((seqId) => {
         if (!rcsbFvRef.current) return;
         if (seqId === null) {
            rcsbFvRef.current.clearSelection('hover');
         } else {
            rcsbFvRef.current.setSelection({ elements: [{ begin: seqId }], mode: 'hover' });
         }
      });
      return unsub;
   }, [subscribeMolstarHover]);

   useEffect(() => {
      if (!activeBaseSequence && activePdbSequences.length === 0) return;

      let cancelled = false;

      const timer = setTimeout(() => {
         if (cancelled) return;
         initRcsb();
         requestAnimationFrame(() => {
            if (wrapperRef.current) {
               wrapperRef.current.style.minHeight = '';
            }
            window.scrollTo({ top: savedScrollRef.current, behavior: 'instant' });
         });
      }, 0);

      return () => {
         cancelled = true;
         clearTimeout(timer);
         observerRef.current?.disconnect();
         observerRef.current = null;
         // Freeze wrapper height before clearing so the page doesn't collapse and scroll-jump
         if (wrapperRef.current) {
            savedScrollRef.current = window.scrollY;
            wrapperRef.current.style.minHeight = `${wrapperRef.current.getBoundingClientRect().height}px`;
         }
         clearContainer();
         if (rcsbFvRef.current) {
            rcsbFvRef.current.unmount();
            rcsbFvRef.current = null;
         }
      };
   }, [activeBaseSequence, activePdbSequences, selectedPdb, selectedUniprot, residueMapping]);

   // Update block track colors (focused/unfocused) without rebuilding the whole viewer
   useEffect(() => {
      if (!rcsbFvRef.current) return;
      const baseSeq = activeBaseSequence ?? (activePdbSequences.length > 0 ? activePdbSequences[0] : null);
      if (!baseSeq) return;
      const pdbSeqs =
         !activeBaseSequence && activePdbSequences.length > 0 ? activePdbSequences.slice(1) : activePdbSequences;
      const { alignments, boardShift } = computeAlignments(baseSeq, pdbSeqs);
      void rcsbFvRef.current.updateBoardConfig({
         rowConfigData: createRowConfig(baseSeq, pdbSeqs, alignments, boardShift),
      });
   }, [focusedStructureRowIds, focusedPocketRowId]);

   /**
    * Computes alignment offsets for each PDB-chain track relative to the UniProt
    * reference sequence. Uses `StructureSequenceDto.uniprotStart` / `uniprotEnd` as
    * the UniProt coordinate range and `StructureSequenceDto.source` as the PDB
    * sequence to FASTA-parse.
    *
    * Returns a list of `AlignmentInfo` objects containing:
    * - `seqStart` — the UniProt position of the first residue of the PDB chain
    * - `seqEnd`   — the UniProt position of the last residue of the PDB chain
    * - `offset`   — the zero-based number of UniProt positions before this chain
    *               starts (used by RCSB as `displayDomain` shift)
    */
   const computeAlignments = (
      baseSeq: StructureSequenceDto,
      pdbSeqs: StructureSequenceDto[],
   ): { alignments: { begin: number; length: number }[]; totalLength: number; boardShift: number } => {
      const baseAlignment = { begin: 1, length: baseSeq.sequence.length };
      const alignments: { begin: number; length: number }[] = [baseAlignment];
      let totalLength = baseAlignment.begin + baseAlignment.length - 1;

      for (const seq of pdbSeqs) {
         let begin: number;
         let length: number;

         if (seq.uniprotStart) {
            begin = seq.uniprotStart;
            length = Math.max(seq.sequence.length, (seq.uniprotEnd ?? seq.uniprotStart) - seq.uniprotStart + 1);
         } else {
            begin = totalLength + 1;
            length = seq.sequence.length;
         }

         alignments.push({ begin, length });
         const end = begin + length - 1;
         if (end > totalLength) totalLength = end;
      }

      return { alignments, totalLength, boardShift: 0 };
   };

   /**
    * Uses a `MutationObserver` to watch the RCSB viewer container and re-injects
    * custom 'remove structure' buttons into each PDB-chain track header every time
    * the RCSB library rebuilds its DOM (which it does asynchronously on data
    * changes). Without this, the custom buttons would disappear after every viewer
    * refresh.
    */
   const injectRemoveButtons = (pdbSeqs: StructureSequenceDto[]): void => {
      if (observerRef.current) {
         observerRef.current.disconnect();
         observerRef.current = null;
      }
      if (!wrapperRef.current || pdbSeqs.length === 0) return;

      const ensureButtons = (): void => {
         if (!wrapperRef.current) return;
         for (const seq of pdbSeqs) {
            const trackEl = wrapperRef.current.querySelector(`[id^="pdbTrack-${seq.rowId}"]`);
            if (!trackEl) continue;

            const rcsbFvRow = trackEl.closest('.rcsbFvRow');
            if (!rcsbFvRow?.parentElement) continue;

            const outerDiv = rcsbFvRow.parentElement;
            if (outerDiv.querySelector('.ahoj-remove-pdb-btn')) continue;

            const titleEl = outerDiv.querySelector('.rcsbFvRowTitleText');
            if (!titleEl) continue;

            const btn = document.createElement('button');
            btn.className = 'ahoj-remove-pdb-btn';
            btn.textContent = '\u00d7';
            btn.title = `Remove ${seq.label ?? seq.id}`;
            btn.style.cssText =
               'cursor: pointer; padding: 0 4px; font-size: 14px; line-height: 1; border: none; background: transparent; color: #999; float: right;';
            btn.addEventListener('click', () => {
               const site = findSiteByRowId(seq.rowId);
               if (site) {
                  if (focusedPocketRowIdRef.current === seq.rowId) {
                     dispatch({ type: 'POCKET_FOCUS', payload: { rowId: seq.rowId, url: site.url, value: false } });
                     setFocusedPocket(null);
                  }
                  if (focusedStructureRowIdsRef.current.has(seq.rowId)) {
                     dispatch({ type: 'FOCUS', payload: { rowId: seq.rowId, url: site.url, value: false } });
                     toggleFocusedStructure(seq.rowId);
                  }
               }
               setActivePdbSequences((prev) => prev.filter((s) => s.rowId !== seq.rowId));
               setSelectedPdb(selectedPdb.filter((p) => p.rowId !== seq.rowId));
            });
            titleEl.appendChild(btn);
         }
      };

      // Inject immediately, then keep observing so buttons survive RCSB's async re-renders
      ensureButtons();
      const observer = new MutationObserver(ensureButtons);
      observerRef.current = observer;
      observer.observe(wrapperRef.current, { childList: true, subtree: true });
   };

   /**
    * Builds the full `RcsbFv` configuration and mounts (or remounts) the sequence
    * viewer in the DOM container. Called whenever the focused structure changes or
    * the pocket residue selection changes.
    *
    * Hover and click callbacks are registered here and forwarded to the Molstar
    * event bus. The `highlightCallback` fires on residue hover; the `clickCallback`
    * fires on residue click.
    */
   const initRcsb = (): void => {
      const baseSeq = activeBaseSequence ?? (activePdbSequences.length > 0 ? activePdbSequences[0] : null);
      if (!baseSeq) return;

      const pdbSeqs =
         !activeBaseSequence && activePdbSequences.length > 0 ? activePdbSequences.slice(1) : activePdbSequences;

      const inner = getOrCreateInner();
      if (!inner) return;

      if (rcsbFvRef.current) {
         rcsbFvRef.current.unmount();
         rcsbFvRef.current = null;
      }

      const { alignments, totalLength, boardShift } = computeAlignments(baseSeq, pdbSeqs);

      const boardConfigData: RcsbFvBoardConfigInterface = {
         length: totalLength,
         includeAxis: true,
         highlightHoverElement: true,
         highlightHoverPosition: true,
         hideInnerBorder: true,
         includeTooltip: true,
         highlightHoverCallback: (elements) => {
            if (hoverDebounceRef.current !== null) clearTimeout(hoverDebounceRef.current);
            const pos = elements.length > 0 ? elements[0].begin : null;
            hoverDebounceRef.current = setTimeout(() => {
               dispatch({ type: 'UNIPROT_HOVER', payload: { uniprotPos: pos } });
            }, 30);
         },
      };
      rcsbFvRef.current = new RcsbFv({
         elementId: sequenceViewerId,
         boardConfigData,
         rowConfigData: createRowConfig(baseSeq, pdbSeqs, alignments, boardShift),
      });
      injectRemoveButtons(baseSeq.source === 'pdb' ? [baseSeq, ...pdbSeqs] : pdbSeqs);
   };

   /**
    * Builds an `RcsbFvRowConfigInterface` for a single RCSB track row.
    *
    * @param rowId       - Row identifier for hover/click callbacks.
    * @param title       - Track label shown on the left.
    * @param sequence    - Full amino-acid sequence string.
    * @param pocketBlocks - Residue number ranges to render as BLOCK annotations.
    * @param alignment   - Optional alignment offset; when provided the track is
    *                      shifted by `alignment.offset` UniProt positions.
    */
   const createRowConfig = (
      baseSeq: StructureSequenceDto,
      pdbSeqs: StructureSequenceDto[],
      alignments: { begin: number; length: number }[],
      boardShift: number,
   ): RcsbFvRowExtendedConfigInterface[] => {
      const rows: RcsbFvRowExtendedConfigInterface[] = [];

      if (baseSeq.source === 'uniprot') {
         const uniprotTrackOrFirstTrack: RcsbFvRowExtendedConfigInterface = {
            boardId: sequenceViewerId,
            trackId: 'firstTrack',
            trackHeight: 20,
            displayType: getRcsbFvDisplayTypeFromFixed(RcsbFvDisplayTypesFixed.SEQUENCE),
            elementClickCallback: (event) => {
               if (!event) return;
               const newPos = event.begin === selectedUniprotPosRef.current ? null : event.begin;
               selectedUniprotPosRef.current = newPos;
               dispatch({ type: 'SELECT_UNIPROT_POSITION', payload: { uniprotPos: newPos } });
            },
            rowTitle: baseSeq.label ?? '',
            trackData: [
               {
                  begin: alignments[0].begin,
                  label: baseSeq.sequence,
               },
            ],
         };

         const uniprotBlockTrack: RcsbFvRowExtendedConfigInterface = {
            boardId: sequenceViewerId,
            trackId: 'firstTrackBlockTrack',
            displayColor: '#9999FF',
            trackHeight: 20,
            displayType: getRcsbFvDisplayTypeFromFixed(RcsbFvDisplayTypesFixed.BLOCK),
            trackData: [
               {
                  begin: alignments[0].begin,
                  end: alignments[0].begin + baseSeq.sequence.length - 1,
               },
            ],
         };

         rows.push(uniprotTrackOrFirstTrack);
         rows.push(uniprotBlockTrack);
      } else {
         // baseSeq is the first PDB (no UniProt selected) — render it as a full interactive PDB track
         rows.push(...makePdbRows(baseSeq, alignments[0], boardShift));
      }

      for (let i = 0; i < pdbSeqs.length; i++) {
         rows.push(...makePdbRows(pdbSeqs[i], alignments[i + 1], boardShift));
      }

      return rows;
   };

   /**
    * Creates RCSB row configs for all PDB tracks of the focused structure.
    * Each PDB chain that has a `StructureSequenceDto` record gets:
    * 1. A SEQUENCE track showing the amino-acid letters.
    * 2. A BLOCK track showing the pocket residues (if a pocket is focused).
    *
    * Chains are aligned to the UniProt reference using `computeAlignments`.
    */
   const makePdbRows = (
      seq: StructureSequenceDto,
      alignment: { begin: number; length: number },
      boardShift: number,
   ): RcsbFvRowExtendedConfigInterface[] => {
      const pdbTrack: RcsbFvRowExtendedConfigInterface = {
         boardId: sequenceViewerId,
         trackId: `pdbTrack-${seq.rowId}`,
         elementId: `pdbTrack-${seq.rowId}`,
         trackHeight: 20,
         displayType: getRcsbFvDisplayTypeFromFixed(RcsbFvDisplayTypesFixed.SEQUENCE),
         elementClickCallback: (event) => {
            if (!event) return;
            const newPos = event.begin === selectedUniprotPosRef.current ? null : event.begin;
            selectedUniprotPosRef.current = newPos;
            dispatch({ type: 'SELECT_UNIPROT_POSITION', payload: { uniprotPos: newPos } });
         },
         rowTitle: seq.label ?? '',
         trackData: [
            {
               begin: alignment.begin,
               label: seq.sequence,
            },
         ],
      };

      const pdbBlockTrack: RcsbFvRowExtendedConfigInterface = {
         boardId: sequenceViewerId,
         trackId: `pdbBlockTrack-${seq.rowId}`,
         trackHeight: 20,
         displayColor: focusedStructureRowIds.has(seq.rowId) ? '#66dd66' : '#dd99ff',
         displayType: getRcsbFvDisplayTypeFromFixed(RcsbFvDisplayTypesFixed.BLOCK),
         elementClickCallback: (event) => {
            if (!event) return;
            const site = findSiteByRowId(seq.rowId);
            if (!site) return;
            const isCurrentlyFocused = focusedStructureRowIdsRef.current.has(seq.rowId);
            if (focusedPocketRowIdRef.current !== null) {
               const pocketSite = findSiteByRowId(focusedPocketRowIdRef.current);
               if (pocketSite)
                  dispatch({
                     type: 'POCKET_FOCUS',
                     payload: { rowId: focusedPocketRowIdRef.current, url: pocketSite.url, value: false },
                  });
               setFocusedPocket(null);
            }
            skipNextFocusSyncRef.current = true;
            dispatch({ type: 'FOCUS', payload: { rowId: seq.rowId, url: site.url, value: !isCurrentlyFocused } });
            toggleFocusedStructure(seq.rowId);
            suppressStructureClickRef.current++;
            dispatchStructureClick(site);
         },
         trackData: [
            {
               begin: alignment.begin,
               end: alignment.begin + alignment.length - 1,
            },
         ],
      };

      const residueMappingForPdb = residueMapping.get(seq.rowId);

      const pdbResidueTrack: RcsbFvRowExtendedConfigInterface = {
         boardId: sequenceViewerId,
         trackId: `pdbResidueTrack-${seq.rowId}`,
         trackHeight: 20,
         displayColor: focusedPocketRowId === seq.rowId ? '#ff4444' : '#ff9999',
         displayType: getRcsbFvDisplayTypeFromFixed(RcsbFvDisplayTypesFixed.BLOCK),
         elementClickCallback: (event) => {
            if (!event) return;
            const site = findSiteByRowId(seq.rowId);
            if (!site) return;
            const isCurrentlyFocused = focusedPocketRowId === seq.rowId;
            if (isCurrentlyFocused) {
               dispatch({ type: 'POCKET_FOCUS', payload: { rowId: seq.rowId, url: site.url, value: false } });
               setFocusedPocket(null);
            } else {
               if (focusedPocketRowId !== null) {
                  const prevSite = findSiteByRowId(focusedPocketRowId);
                  if (prevSite)
                     dispatch({
                        type: 'POCKET_FOCUS',
                        payload: { rowId: focusedPocketRowId, url: prevSite.url, value: false },
                     });
               }
               dispatch({ type: 'POCKET_FOCUS', payload: { rowId: seq.rowId, url: site.url, value: true } });
               setFocusedPocket(seq.rowId);
            }
         },
         rowTitle: 'Pocket Residues',
         trackData: residueMappingForPdb
            ? groupConsecutiveNumbers(residueMappingForPdb.residues).map((group) => ({
                 begin: group[0] + boardShift,
                 end: group[group.length - 1] + boardShift,
              }))
            : [],
      };

      return [pdbTrack, pdbBlockTrack, pdbResidueTrack];
   };

   return (
      <div
         ref={wrapperRef}
         style={{ width: '100%', overflow: 'hidden' }}
         onMouseLeave={(): void => {
            if (hoverDebounceRef.current !== null) clearTimeout(hoverDebounceRef.current);
            dispatch({ type: 'UNIPROT_HOVER', payload: { uniprotPos: null } });
         }}
      />
   );
};

/**
 * Parses the **first** FASTA record from a multi-FASTA string and returns a
 * `StructureSequenceDto` for it. Returns `null` when the text contains no
 * sequence data.
 *
 * Only the first record is parsed intentionally: the backend returns one FASTA
 * per chain, so there is exactly one record per call. Stopping at the first
 * `>` header after the initial one prevents accidentally concatenating multi-
 * chain FASTA files.
 */
const parseFastaFirst = (
   text: string,
   id: string,
   rowId: string,
   source: 'pdb' | 'uniprot',
): StructureSequenceDto | null => {
   let label: string | null = null;
   const lines = text.split('\n');
   const sequenceLines: string[] = [];

   for (const line of lines) {
      if (line.startsWith('>')) {
         if (label !== null) break;
         label = line.slice(1).trim();
      } else if (label !== null) {
         sequenceLines.push(line.trim());
      }
   }

   const sequence = sequenceLines.join('');
   if (!sequence) return null;
   return { id, rowId, label: id, sequence, source } as StructureSequenceDto;
};

enum RcsbFvDisplayTypesFixed {
   BLOCK,
   PIN,
   SEQUENCE,
   AREA,
   COMPOSITE,
}

/**
 * Converts a numeric `RcsbFvDisplayTypesFixed` enum value to the string literal
 * that RCSB Saguaro expects in its row configuration.
 *
 * Exists because `@rcsb/rcsb-saguaro` does not export the `RcsbFvDisplayTypes`
 * enum values as constants, so they must be mapped manually.
 */
function getRcsbFvDisplayTypeFromFixed(displayType: RcsbFvDisplayTypesFixed): RcsbFvDisplayTypes {
   const mapping = {
      [RcsbFvDisplayTypesFixed.BLOCK]: 'block',
      [RcsbFvDisplayTypesFixed.AREA]: 'area',
      [RcsbFvDisplayTypesFixed.SEQUENCE]: 'sequence',
      [RcsbFvDisplayTypesFixed.PIN]: 'pin',
      [RcsbFvDisplayTypesFixed.COMPOSITE]: 'composite',
   };

   return mapping[displayType] as RcsbFvDisplayTypes;
}

/**
 * Groups an array of residue number strings into contiguous runs.
 * For example `['10','11','12','15']` → `[[10,11,12],[15]]`.
 *
 * The RCSB block annotation requires `{ begin, end }` objects, so each group is
 * represented as an array whose first element is `begin` and last is `end`.
 */
const groupConsecutiveNumbers = (residues: string[]): number[][] => {
   const groups: number[][] = [];
   for (const s of residues) {
      const n = parseInt(s, 10);
      if (groups.length > 0 && n === groups[groups.length - 1][groups[groups.length - 1].length - 1] + 1) {
         groups[groups.length - 1].push(n);
      } else {
         groups.push([n]);
      }
   }
   return groups;
};

export default SequenceViewerComponent;
